import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { tmApi } from '@/lib/api'
import { formatDate } from '@/lib/utils'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Trash2, Upload, Database, AlertCircle, FileText, ChevronLeft, ChevronRight } from 'lucide-react'
import { toast } from 'sonner'
import { useRef, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { ScrollArea } from '@/components/ui/scroll-area'

/**
 * TM 导入结果（用于在 Dialog 中展示明细）
 */
type ImportResult = {
  inserted: number
  duplicated: number
  skippedUnknownLang: number
  totalParsed: number
  files: Array<{
    name: string
    sourceLang: string
    targetLang: string
    count: number
    skipped: number
  }>
  skippedFiles: Array<{ name: string; reason: string }>
}

/**
 * 翻译记忆库（TM）管理组件
 *
 * 功能：
 *   - 上传 ZIP 包批量导入双列双语 CSV（表头语言码自动归一化为系统两位码）
 *   - 按语言对分组展示统计
 *   - 分页查看 TM 列表，支持搜索 + 语言对筛选
 *   - 删除单条 / 清空所有（带二次确认）
 *
 * 仅在客户已启用 TM 时可用（父组件 ClientDetail 控制）。
 */
export function TMManager({ clientId }: { clientId: string }) {
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)

  // 列表筛选状态
  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)
  const [search, setSearch] = useState('')
  const [filterSourceLang, setFilterSourceLang] = useState('')
  const [filterTargetLang, setFilterTargetLang] = useState('')

  // 上传进度
  const [uploadProgress, setUploadProgress] = useState(0)
  // 导入结果 Dialog
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  // 清空确认 Dialog
  const [clearDialogOpen, setClearDialogOpen] = useState(false)

  // 查询统计
  const { data: statsResponse, isLoading: statsLoading } = useQuery({
    queryKey: ['tm-stats', clientId],
    queryFn: () => tmApi.getStats(clientId),
  })
  const stats = statsResponse?.data

  // 查询列表
  const { data: listResponse, isLoading: listLoading } = useQuery({
    queryKey: ['tm-list', clientId, page, pageSize, search, filterSourceLang, filterTargetLang],
    queryFn: () =>
      tmApi.list(clientId, {
        page,
        pageSize,
        search: search || undefined,
        sourceLang: filterSourceLang || undefined,
        targetLang: filterTargetLang || undefined,
      }),
  })
  const listData = listResponse?.data
  const items = listData?.items || []
  const pagination = listData?.pagination

  // 导入 mutation
  const importMutation = useMutation({
    mutationFn: (file: File) =>
      tmApi.import(clientId, file, (p) => setUploadProgress(p)),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['tm-stats', clientId] })
      queryClient.invalidateQueries({ queryKey: ['tm-list', clientId] })
      setUploadProgress(0)
      const data = res.data
      if (data) {
        setImportResult(data)
      }
      toast.success(res.message || '导入完成')
    },
    onError: (error: any) => {
      setUploadProgress(0)
      toast.error(error.error || error.message || '导入失败')
    },
  })

  // 删除单条
  const deleteMutation = useMutation({
    mutationFn: (id: string) => tmApi.delete(clientId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tm-stats', clientId] })
      queryClient.invalidateQueries({ queryKey: ['tm-list', clientId] })
      toast.success('TM 记录已删除')
    },
    onError: (error: any) => {
      toast.error(error.error || '删除失败')
    },
  })

  // 清空所有
  const clearAllMutation = useMutation({
    mutationFn: () => tmApi.clearAll(clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tm-stats', clientId] })
      queryClient.invalidateQueries({ queryKey: ['tm-list', clientId] })
      setClearDialogOpen(false)
      toast.success('已清空所有 TM 记录')
    },
    onError: (error: any) => {
      toast.error(error.error || '清空失败')
    },
  })

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      importMutation.mutate(file)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setPage(1)
  }

  return (
    <div className="space-y-4">
      {/* 操作栏 */}
      <div className="flex justify-between items-center flex-wrap gap-2">
        <h3 className="text-lg font-medium flex items-center gap-2">
          <Database className="h-5 w-5" />
          翻译记忆库
          {stats && (
            <span className="text-sm text-muted-foreground font-normal">
              共 {stats.total} 条
            </span>
          )}
        </h3>
        <div className="flex gap-2">
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept=".zip"
            onChange={handleFileChange}
          />
          <Button
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={importMutation.isPending}
          >
            <Upload className="mr-2 h-4 w-4" />
            {importMutation.isPending ? '导入中...' : '导入 ZIP'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setClearDialogOpen(true)}
            disabled={clearAllMutation.isPending || !stats?.total}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            清空
          </Button>
        </div>
      </div>

      {/* 上传进度 */}
      {uploadProgress > 0 && uploadProgress < 100 && (
        <div className="space-y-1">
          <Progress value={uploadProgress} className="h-2" />
          <p className="text-xs text-muted-foreground">
            上传中 {uploadProgress}%
          </p>
        </div>
      )}

      {/* 使用说明 */}
      <Card className="bg-muted/30 border-dashed">
        <CardContent className="pt-4 pb-4 text-sm text-muted-foreground space-y-1">
          <p className="font-medium text-foreground">ZIP 导入格式说明：</p>
          <p>• ZIP 包内放置一个或多个 .csv 文件，每个文件双列双语对照</p>
          <p>• 第一行表头为语言代码，例如 <code className="bg-muted px-1 rounded">en-US</code>、<code className="bg-muted px-1 rounded">zh-Hans</code>，系统自动归一化为两位码（en、zh）</p>
          <p>• 第二行起：第 1 列源文、第 2 列译文，空行自动跳过（自动识别 UTF-8 BOM 和逗号/制表符分隔）</p>
          <p>• 重复导入相同内容会自动跳过（按 客户+语言对+源文 去重）</p>
        </CardContent>
      </Card>

      {/* 按语言对统计 */}
      {statsLoading ? (
        <div className="text-sm text-muted-foreground">加载统计中...</div>
      ) : stats && stats.groups.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {stats.groups.map((g) => (
            <Card key={`${g.sourceLang}-${g.targetLang}`}>
              <CardContent className="pt-4 pb-4">
                <div className="text-xs font-mono uppercase text-muted-foreground">
                  {g.sourceLang} → {g.targetLang}
                </div>
                <div className="text-2xl font-bold mt-1">{g.count}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-sm text-muted-foreground py-4 text-center">
          暂无 TM 记录，点击"导入 ZIP"上传第一批双语 CSV
        </div>
      )}

      {/* 筛选 + 列表 */}
      {stats && stats.total > 0 && (
        <>
          <form
            onSubmit={handleSearchSubmit}
            className="flex gap-2 items-center flex-wrap"
          >
            <Input
              placeholder="搜索源文或译文..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-xs"
            />
            <Input
              placeholder="源语言（如 en）"
              value={filterSourceLang}
              onChange={(e) => setFilterSourceLang(e.target.value)}
              className="w-32"
            />
            <Input
              placeholder="目标语言（如 zh）"
              value={filterTargetLang}
              onChange={(e) => setFilterTargetLang(e.target.value)}
              className="w-32"
            />
            <Button type="submit" size="sm" variant="outline">筛选</Button>
            {(search || filterSourceLang || filterTargetLang) && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setSearch('')
                  setFilterSourceLang('')
                  setFilterTargetLang('')
                  setPage(1)
                }}
              >
                重置
              </Button>
            )}
          </form>

          <div className="border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">语言</TableHead>
                  <TableHead>源文 (Source)</TableHead>
                  <TableHead>译文 (Target)</TableHead>
                  <TableHead className="w-32">创建时间</TableHead>
                  <TableHead className="w-16 text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {listLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      加载中...
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      无匹配记录
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="text-xs font-mono uppercase">
                        <span className="bg-muted px-1.5 py-0.5 rounded text-muted-foreground">
                          {item.sourceLang} → {item.targetLang}
                        </span>
                      </TableCell>
                      <TableCell className="font-medium max-w-md truncate" title={item.sourceText}>
                        {item.sourceText}
                      </TableCell>
                      <TableCell className="max-w-md truncate" title={item.targetText}>
                        {item.targetText}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {formatDate(item.createdAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteMutation.mutate(item.id)}
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* 分页 */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                第 {pagination.page} / {pagination.totalPages} 页，共 {pagination.total} 条
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={page >= pagination.totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* 导入结果 Dialog */}
      <Dialog open={!!importResult} onOpenChange={(open) => !open && setImportResult(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>TM 导入结果</DialogTitle>
            <DialogDescription>
              共解析 {importResult?.totalParsed} 条翻译对，新增 {importResult?.inserted} 条
              {importResult && importResult.duplicated > 0 && `，跳过重复 ${importResult.duplicated} 条`}
              {importResult && importResult.skippedUnknownLang > 0 && `，语言码识别失败 ${importResult.skippedUnknownLang} 条`}
            </DialogDescription>
          </DialogHeader>

          {importResult && (
            <ScrollArea className="max-h-[60vh]">
              <div className="space-y-4 pr-2">
                {/* 文件明细 */}
                <div className="space-y-2">
                  <h4 className="text-sm font-medium flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    文件明细（{importResult.files.length} 个文件）
                  </h4>
                  <div className="border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>文件名</TableHead>
                          <TableHead className="w-32">语言对</TableHead>
                          <TableHead className="w-20 text-right">导入</TableHead>
                          <TableHead className="w-20 text-right">跳过</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {importResult.files.map((f) => (
                          <TableRow key={f.name}>
                            <TableCell className="font-mono text-xs truncate" title={f.name}>
                              {f.name}
                            </TableCell>
                            <TableCell className="text-xs font-mono uppercase">
                              {f.sourceLang} → {f.targetLang}
                            </TableCell>
                            <TableCell className="text-right">{f.count}</TableCell>
                            <TableCell className="text-right text-muted-foreground">
                              {f.skipped || '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* 失败文件 */}
                {importResult.skippedFiles.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium flex items-center gap-2 text-destructive">
                      <AlertCircle className="h-4 w-4" />
                      解析失败文件（{importResult.skippedFiles.length} 个）
                    </h4>
                    <div className="border border-destructive/30 rounded-md bg-destructive/5">
                      <Table>
                        <TableBody>
                          {importResult.skippedFiles.map((f) => (
                            <TableRow key={f.name}>
                              <TableCell className="font-mono text-xs">{f.name}</TableCell>
                              <TableCell className="text-xs text-destructive">{f.reason}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          )}

          <DialogFooter>
            <Button onClick={() => setImportResult(null)}>关闭</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 清空确认 Dialog */}
      <Dialog open={clearDialogOpen} onOpenChange={setClearDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>确认清空 TM</DialogTitle>
            <DialogDescription>
              此操作将删除该客户的所有翻译记忆库记录（共 {stats?.total || 0} 条），无法撤销。
              项目翻译时将不再从此客户的历史 TM 中匹配。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClearDialogOpen(false)}>
              取消
            </Button>
            <Button
              variant="destructive"
              onClick={() => clearAllMutation.mutate()}
              disabled={clearAllMutation.isPending}
            >
              {clearAllMutation.isPending ? '清空中...' : '确认清空'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
